import {DndContext, DragOverlay, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus, DropdownItem, DropdownSubmenu, showNotification} from 'platform/components';
import {Box, Heading, Stack, Icon, Show, Spinner, Text, VStack} from 'platform/foundation';
import {Lightbox} from 'platform/lightbox';
import {useDateTimeFormatter} from 'platform/locale';

import {useCallback, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {groupBy, isNotNil, partition} from 'ramda';
import {isNonEmptyArray, isNotNilOrEmpty, isTrue} from 'ramda-adjunct';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {useGetParticipationQuery} from '@dms/api/core';
import {useGetVehicleQuery, useGetWatermarkListQuery} from '@dms/api/sales';
import {EntityResourceIds, VehicleAlbums} from '@dms/api/shared';
import {
  saleVehiclePhotoApi,
  useCopySaleVehiclePhotoMutation,
  useGetSaleVehicleAlbumPhotoCheckerReportQuery,
  useGetSaleVehicleAlbumPhotoListQuery,
  useGetSaleVehicleQuery,
  useListSaleVehicleAlbumsByVehicleQuery,
  useUpdateSaleVehiclePhotoAlbumMutation,
} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import {getSortableContainerId, parseDndItemId} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {notificationTypes, handleApiError, usePermissions} from '@dms/shared';

import {parseDate, useRequiredParams} from 'shared/web';

import {BannerBgRemoval} from '../../components/BannerBgRemoval/BannerBgRemoval';
import {IMAGE_SIZE} from '../../constants/imageSize';
import {useVehicleAlbumWatermarkPreview} from '../../hooks/useVehicleAlbumWatermarkPreview';
import {Album} from './Album';
import {AlbumImage} from './AlbumImage';
import {BackgroundRemovalStatus} from './BackgroundRemovalStatus';
import {PhotoCheckerWidget} from './PhotoCheckerWidget';
import {getNotificationText} from './utils/getNotificationText';
import {buildPhotoEvalMap} from './utils/matchPhotoEval';
import {prepareAlbumImageUrl} from './utils/prepareAlbumImageUrl';

export interface VehiclePhotoGalleryProps {
  onVehiclePhotosUpdate?: () => void;
}

const PHOTO_CHECKER_WIDGET_WIDTH = 84;

export const VehiclePhotoGallery = (props: VehiclePhotoGalleryProps) => {
  const {id: vehicleId} = useRequiredParams();
  const formatDate = useDateTimeFormatter();

  // useDispatch is not correctly typed, so any is used to override errors thrown from the updateQueryData function
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();

  const [activeDnDUrl, setActiveDnDUrl] = useState<string | null>(null);
  const [isDropAnimationDisabled, setIsDropAnimationDisabled] = useState(false);
  const dragSourceContainerRef = useRef<string | null>(null);

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: vehicleId,
  });

  const {data: vehicle} = useGetVehicleQuery({vehicleId});
  const {data: saleVehicle} = useGetSaleVehicleQuery({vehicleId});

  const [
    hasRemoveVehiclePhotoBackgroundPermission,
    hasSaleVehicleBackgroundRemovalPermission,
    canReadPhotoCheckerReport,
  ] = usePermissions({
    permissionKeys: [
      'removeVehiclePhotoBackground',
      'saleVehicleBackgroundRemoval',
      'readPhotoCheckerReport',
    ],
    scopes: {
      removeVehiclePhotoBackground: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  const {data: watermarkList} = useGetWatermarkListQuery();

  const {
    getLightboxProps,
    openWatermarkPreview,
    isLoading: isWatermarkPreviewLoading,
  } = useVehicleAlbumWatermarkPreview();

  const [hasBackgroundRemovalErrorFileOperationIds, setHasBackgroundRemovalErrorFileOperationIds] =
    useState<Set<string>>(new Set());
  const [hasBackgroundRemovalInProgressAlbumsIds, setHasBackgroundRemovalInProgressAlbumsIds] =
    useState<Set<string>>(new Set());

  const [globallySelectedImagesIds, setGloballySelectedImagesIds] = useState<
    Record<string, string[]>
  >({});

  const handleRefetchSaleVehiclePhotos = () => {
    dispatch(
      saleVehiclePhotoApi.util.invalidateTags([
        {type: 'SaleVehicleAlbumPhotos'},
        {type: 'Vehicle'},
        {type: 'SaleVehicleFlags'},
      ])
    );
  };

  useSubscribeToNotificationsQuery((notification) => {
    if (notification.type === 'file_storage-file_uploaded') {
      handleRefetchSaleVehiclePhotos();
    }

    if (notification.type === notificationTypes.SALE_VEHICLE_BACKGROUND_REMOVAL_FINISHED) {
      handleRefetchSaleVehiclePhotos();
    }

    if (notification.variant === 'background') {
      const notificationText = getNotificationText(notification);
      if (isNotNil(notificationText)) {
        showNotification.success(notificationText);
      }

      dispatch(
        saleVehiclePhotoApi.util.invalidateTags([
          {type: 'SaleVehicleAlbumPhotos'},
          {type: 'Vehicle'},
          {type: 'SaleVehicleFlags'},
        ])
      );
    }
  });

  const {
    data: albums,
    isLoading: areAlbumsLoading,
    isError: hasAlbumsError,
  } = useListSaleVehicleAlbumsByVehicleQuery({
    vehicleId,
  });

  const [activeAlbums, previousAlbums] = partition(
    (album) => isTrue(album.active),
    (albums ?? []).filter((album) => album.name !== VehicleAlbums.PHOTOS_SERVICE_CASE)
  );

  const previousAlbumIds = previousAlbums.map((album) => album.id);

  const previousAlbumsGroupedByDate = groupBy(
    (album) => album.saleVehicleCreatedAt,
    previousAlbums
  );

  const salesAlbum = activeAlbums.find((album) => album.name === VehicleAlbums.PHOTOS_CAR_SALES);

  const {data: salesAlbumPhotos, isLoading: isLoadingSalesAlbumPhotos} =
    useGetSaleVehicleAlbumPhotoListQuery(
      isNotNil(salesAlbum) && canReadPhotoCheckerReport
        ? {saleVehicleId: salesAlbum.saleVehicleId, saleVehicleAlbumId: salesAlbum.id}
        : skipToken
    );

  const hasPhotoCheckerPhotos = !isLoadingSalesAlbumPhotos && (salesAlbumPhotos?.length ?? 0) > 0;

  const {data: photoCheckerReport} = useGetSaleVehicleAlbumPhotoCheckerReportQuery(
    isNotNil(salesAlbum) && canReadPhotoCheckerReport
      ? {saleVehicleAlbumId: salesAlbum.id}
      : skipToken
  );

  const photoEvalMap = useMemo(
    () =>
      photoCheckerReport?.result?.evals ? buildPhotoEvalMap(photoCheckerReport.result.evals) : {},
    [photoCheckerReport]
  );

  const [copySaleVehiclePhoto] = useCopySaleVehiclePhotoMutation();
  const [updateSaleVehicleAlbum] = useUpdateSaleVehiclePhotoAlbumMutation();

  const copyImage = ({
    fileIds,
    sourceAlbumId,
    destinationAlbum,
    saleVehicleId,
  }: {
    fileIds: string[];
    saleVehicleId: string;
    sourceAlbumId: string;
    destinationAlbum: {
      id: string;
      name: string;
    };
    position?: number;
  }) => {
    if (isNonEmptyArray(fileIds)) {
      return copySaleVehiclePhoto({
        saleVehicleId,
        saleVehiclePhotoCopyRequestBody: {
          photos: fileIds.map((fileId, index) => ({
            fileId,
            position: index,
          })),
          sourceAlbum: sourceAlbumId,
          destinationAlbum: destinationAlbum.id,
        },
      })
        .unwrap()
        .then(() => {
          if (destinationAlbum.name === VehicleAlbums.PHOTOS_CAR_SALES) {
            props.onVehiclePhotosUpdate?.();
          }
        })
        .catch(handleApiError);
    }
  };

  const moveImage = ({
    fileIds,
    sourceAlbumId,
    destinationAlbum,
  }: {
    fileIds: string[];
    sourceAlbumId: string;
    destinationAlbum: {
      id: string;
      name: string;
    };
  }) => {
    if (isNonEmptyArray(fileIds)) {
      return updateSaleVehicleAlbum({
        saleVehiclePhotoUpdateAlbumRequestBody: {
          photos: fileIds.map((fileId, index) => ({
            fileId,
            position: index,
          })),
          sourceAlbum: sourceAlbumId,
          destinationAlbum: destinationAlbum.id,
        },
      })
        .unwrap()
        .then(() => {
          if (destinationAlbum.name === VehicleAlbums.PHOTOS_CAR_SALES) {
            props.onVehiclePhotosUpdate?.();
          }
        })
        .catch(handleApiError);
    }
  };

  const buildContextMenu = (payload: {
    images: string[];
    sourceAlbumId: string;
    saleVehicleId: string;
    onSuccess?: () => void;
    onEventFired?: () => void;
    isSalesAlbum?: boolean;
  }) => (
    <>
      <DropdownSubmenu
        label={i18n.t('general.actions.copyTo')}
        prefix={<Icon value="file/folder" />}
        suffix={<Icon value="navigation/arrow_right" />}
      >
        {activeAlbums?.map((album) =>
          album.id === payload.sourceAlbumId ? null : (
            <DropdownItem
              key={`copy_${album.id}`}
              label={album.translation}
              onClick={() => {
                payload.onEventFired?.();
                copyImage({
                  fileIds: payload.images || [],
                  sourceAlbumId: payload.sourceAlbumId,
                  destinationAlbum: {
                    id: album.id,
                    name: album.name,
                  },
                  saleVehicleId: payload.saleVehicleId,
                  position: 0,
                })?.then(() => payload.onSuccess?.());
              }}
            />
          )
        )}
      </DropdownSubmenu>
      <DropdownSubmenu
        label={i18n.t('general.actions.moveTo')}
        prefix={<Icon value="file/folder" />}
        suffix={<Icon value="navigation/arrow_right" />}
      >
        {activeAlbums?.map((album) =>
          album.id === payload.sourceAlbumId ? null : (
            <DropdownItem
              key={`copy_${album.id}`}
              label={album.translation}
              onClick={() => {
                payload.onEventFired?.();
                if (previousAlbumIds.includes(payload.sourceAlbumId)) {
                  return copyImage({
                    fileIds: payload.images || [],
                    sourceAlbumId: payload.sourceAlbumId,
                    destinationAlbum: {
                      id: album.id,
                      name: album.name,
                    },
                    saleVehicleId: payload.saleVehicleId,
                    position: 0,
                  })?.then(() => payload.onSuccess?.());
                }
                return moveImage({
                  fileIds: payload.images || [],
                  sourceAlbumId: payload.sourceAlbumId,
                  destinationAlbum: {
                    id: album.id,
                    name: album.name,
                  },
                })?.then(() => payload.onSuccess?.());
              }}
            />
          )
        )}
      </DropdownSubmenu>
      <Show
        when={
          payload.images.length === 1 &&
          payload.isSalesAlbum &&
          isNotNilOrEmpty(watermarkList) &&
          isNotNilOrEmpty(saleVehicle?.watermarkId)
        }
      >
        <DropdownItem
          label={i18n.t('entity.vehicle.labels.showWithWatermark')}
          prefix={<Icon value="AV/branding_watermark" />}
          onClick={() => openWatermarkPreview(payload.images[0])}
        />
      </Show>
    </>
  );

  // useCallback with [] is required for correctness, not performance: these callbacks are
  // passed as props to Album where they appear in useEffect dependency arrays. Without a
  // stable reference, every parent re-render creates a new function, the child effect fires,
  // calls setState here, triggering another re-render — infinite loop.
  const handleBackgroundRemoval = useCallback(
    (albumId: string, isInProgress: boolean, hasErrorOperationIds?: string[]) => {
      if (hasErrorOperationIds?.length) {
        setHasBackgroundRemovalErrorFileOperationIds(
          (prev) => new Set([...prev, ...hasErrorOperationIds])
        );
      }
      if (isInProgress) {
        setHasBackgroundRemovalInProgressAlbumsIds((prev) => new Set([...prev, albumId]));
      } else {
        setHasBackgroundRemovalInProgressAlbumsIds((prev) => {
          const newInProgressAlbums = new Set(prev);
          newInProgressAlbums.delete(albumId);
          return newInProgressAlbums;
        });
      }
    },
    []
  );

  // useCallback with [] is required for correctness, not performance: passed as a prop to Album
  // where it appears in a useEffect dependency array. Without a stable reference, every parent
  // re-render creates a new function, the child effect fires, calls setState here, triggering
  // another re-render — infinite loop.
  const handleUpdateGlobalImageSelection = useCallback((albumId: string, ids: string[]) => {
    setGloballySelectedImagesIds((prev) => ({
      ...prev,
      [albumId]: ids,
    }));
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );

  return (
    <>
      <VStack spacing={4}>
        <Show
          when={
            hasRemoveVehiclePhotoBackgroundPermission &&
            isFeatureEnabled(featureFlags.SALES_BGR_BANNER)
          }
        >
          <BannerBgRemoval />
        </Show>

        <Show
          when={
            hasRemoveVehiclePhotoBackgroundPermission && hasSaleVehicleBackgroundRemovalPermission
          }
        >
          <BackgroundRemovalStatus
            isInProgress={hasBackgroundRemovalInProgressAlbumsIds.size > 0}
            failedOperationsIds={Array.from(hasBackgroundRemovalErrorFileOperationIds)}
            onAcknowledgeOperation={() => {
              setHasBackgroundRemovalErrorFileOperationIds(new Set());
            }}
          />
        </Show>
        <DataStatus isLoading={areAlbumsLoading} isError={hasAlbumsError}>
          <DndContext
            sensors={sensors}
            onDragStart={(event) => {
              const {url} = parseDndItemId(event.active.id);
              if (url) {
                setActiveDnDUrl(url);
              }
              dragSourceContainerRef.current = getSortableContainerId(event.active);
              setIsDropAnimationDisabled(false);
            }}
            onDragOver={(event) => {
              if (event.over) {
                const overContainerId =
                  getSortableContainerId(event.over) ?? parseDndItemId(event.over.id).id;
                const isDifferentAlbum = overContainerId !== dragSourceContainerRef.current;
                if (isDifferentAlbum !== isDropAnimationDisabled) {
                  setTimeout(() => setIsDropAnimationDisabled(isDifferentAlbum), 0);
                }
              }
            }}
            onDragEnd={() => {
              dragSourceContainerRef.current = null;
            }}
          >
            {activeAlbums?.map((album) => {
              const isSalesAlbum = album.name === VehicleAlbums.PHOTOS_CAR_SALES;
              const showPhotoChecker =
                isSalesAlbum && canReadPhotoCheckerReport && hasPhotoCheckerPhotos;

              const albumComponent = (
                <Album
                  key={album.id}
                  isActiveSaleVehicle
                  data-testid={testIds.vehicles.photos(
                    `${album.name === VehicleAlbums.PHOTOS_CAR_PURCHASE ? 'purchase' : 'sales'}-album`
                  )}
                  saleVehicleId={album.saleVehicleId}
                  isSalesAlbum={isSalesAlbum}
                  vehicleId={vehicleId}
                  saleVehicleAlbumId={album.id}
                  title={album.translation}
                  canUpload
                  canDelete={album.name === VehicleAlbums.PHOTOS_CUSTOM}
                  showPromotionalPhotos={isSalesAlbum}
                  buildAlbumActionsContextMenu={buildContextMenu}
                  globallySelectedImagesIds={globallySelectedImagesIds}
                  inactiveAlbumIds={previousAlbumIds}
                  onBackgroundRemoval={handleBackgroundRemoval}
                  onUpdateGlobalImageSelection={handleUpdateGlobalImageSelection}
                  canApplyWatermark={isSalesAlbum}
                  onAlbumChange={isSalesAlbum ? props.onVehiclePhotosUpdate : undefined}
                  photoEvalMap={showPhotoChecker ? photoEvalMap : undefined}
                />
              );

              if (showPhotoChecker) {
                return (
                  <Stack
                    key={album.id}
                    direction={['column', 'column', 'row']}
                    spacing={4}
                    align="flex-start"
                  >
                    <Box flex={1}>{albumComponent}</Box>
                    <Box width={['100%', '100%', PHOTO_CHECKER_WIDGET_WIDTH]}>
                      <PhotoCheckerWidget
                        saleVehicleAlbumId={album.id}
                        data-testid={testIds.vehicles.photos('photo-checker-widget')}
                      />
                    </Box>
                  </Stack>
                );
              }

              return albumComponent;
            })}
            <Show when={previousAlbums?.length > 0}>
              <Heading size={3}>{i18n.t('entity.vehicle.labels.previousPhotos')}</Heading>
              <Text size="xSmall">{i18n.t('entity.vehicle.labels.previousPhotosExplainer')}</Text>
              {Object.entries(previousAlbumsGroupedByDate).map(([date, albums]) => (
                <div key={date}>
                  <Heading size={4}>
                    {i18n.t(`entity.vehicle.albums.createdAt`, {
                      date: formatDate('dateShort', parseDate(date)),
                    })}
                  </Heading>
                  {albums?.map((album) => (
                    <Box key={album.id} paddingTop={4}>
                      <Album
                        data-testid={testIds.vehicles.photos(
                          `${album.name === 'PHOTOS_CAR_PURCHASE' ? 'purchase' : 'sales'}-previous-album`
                        )}
                        vehicleId={vehicleId}
                        isActiveSaleVehicle={false}
                        saleVehicleId={album.saleVehicleId}
                        saleVehicleAlbumId={album.id}
                        title={album.translation}
                        canUpload={false}
                        inactiveAlbumIds={previousAlbumIds}
                        isSalesAlbum={album.name === VehicleAlbums.PHOTOS_CAR_SALES}
                        buildAlbumActionsContextMenu={buildContextMenu}
                        onBackgroundRemoval={handleBackgroundRemoval}
                        onUpdateGlobalImageSelection={handleUpdateGlobalImageSelection}
                      />
                    </Box>
                  ))}
                </div>
              ))}
            </Show>
            <DragOverlay dropAnimation={isDropAnimationDisabled ? null : undefined}>
              <Show when={isNotNil(activeDnDUrl)}>
                <Box width={IMAGE_SIZE.width} height={IMAGE_SIZE.height}>
                  <AlbumImage id="sortable:dragOverlay" src={prepareAlbumImageUrl(activeDnDUrl!)} />
                </Box>
              </Show>
            </DragOverlay>
          </DndContext>
          <Lightbox
            data-testid={testIds.vehicles.photos('watermarkPreview')}
            {...getLightboxProps()}
          />
        </DataStatus>
      </VStack>
      <Show when={isWatermarkPreviewLoading}>
        <Spinner variant="overlay" />
      </Show>
    </>
  );
};
